import React, { useState } from 'react';
import { ChromePicker } from 'react-color';
import { Panel, Checkbox, Button, AlertVariant } from '@patternfly/react-core';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { TZoneSetting, ZoneSetting, ZoneSettingEnum } from '../../api/zone/ZoneSetting';
import { useMount } from 'react-use';
import { useApplication } from '../../components/user/ApplicationProvider';

const defaultPalette = [
	{ r: 255, g: 105, b: 0, a: 1 }, // #FF6900
	{ r: 252, g: 185, b: 0, a: 1 }, // #FCB900
	{ r: 123, g: 220, b: 181, a: 1 }, // #7BDCB5
	{ r: 0, g: 208, b: 132, a: 1 }, // #00D084
	{ r: 142, g: 209, b: 252, a: 1 }, // #8ED1FC
	{ r: 6, g: 147, b: 227, a: 1 }, // #0693E3
	{ r: 171, g: 184, b: 195, a: 1 }, // #ABB8C3
	{ r: 235, g: 20, b: 76, a: 1 }, // #EB144C
	{ r: 247, g: 141, b: 167, a: 1 }, // #F78DA7
	{ r: 153, g: 0, b: 239, a: 1 }, // #9900EF

	{ r: 255, g: 221, b: 51, a: 1 }, // #FFDD33 (Yellow)
	{ r: 255, g: 102, b: 102, a: 1 }, // #FF6666 (Light Red)
	{ r: 255, g: 153, b: 51, a: 1 }, // #FF9933 (Deep Orange)
	{ r: 0, g: 204, b: 204, a: 1 }, // #00CCCC (Teal)
	{ r: 153, g: 204, b: 255, a: 1 }, // #99CCFF (Baby Blue)
	{ r: 204, g: 153, b: 255, a: 1 }, // #CC99FF (Lavender Purple)
	{ r: 255, g: 204, b: 204, a: 1 }, // #FFCCCC (Pale Pink)
	{ r: 102, g: 204, b: 102, a: 1 }, // #66CC66 (Soft Green)
	{ r: 255, g: 204, b: 102, a: 1 }, // #FFCC66 (Warm Light Orange)
	{ r: 102, g: 153, b: 204, a: 1 }, // #6699CC (Slate Blue)
];

export default function ZoneColors() {
	const [resetOnSave, setResetOnSave] = useState(false);
	const [colorPaletteSetting, setColorPaletteSetting] = useState<TZoneSetting>(
		ZoneSetting.DefaultNewZoneSetting() as TZoneSetting
	);
	const { addToast } = useToast();
	const { zoneSettings } = useApplication();

	const [colors, setColors] = useState(
		Array.from({ length: 20 }, (_, i) => ({
			id: i + 1,
			color: defaultPalette[i],
			displayPicker: false,
		}))
	);

	useMount(() => {
		const highchartsColorPaletteZoneSetting = zoneSettings.find(
			(x) => x.zone_setting_enum_id == ZoneSettingEnum.defaultColorPalette
		);

		if (highchartsColorPaletteZoneSetting && highchartsColorPaletteZoneSetting.value) {
			setColorPaletteSetting(highchartsColorPaletteZoneSetting);
			const colors = highchartsColorPaletteZoneSetting.value;

			const hexMap = JSON.parse(colors) as Record<string, string>;

			// Convert the object to an array of hex values ordered by key
			const hexArray = Object.keys(hexMap)
				.sort((a, b) => Number(a) - Number(b))
				.map((key) => hexMap[key]);

			const convertedColors = hexArray.map((hex, idx) => ({
				id: idx + 1,
				color: hexToRgba(hex),
				displayPicker: false,
			}));

			setColors(convertedColors);
		}
	});

	const handleSave = () => {
		if (resetOnSave) {
			const newColors = colors.map((entry, idx) => ({
				...entry,
				color: defaultPalette[idx % defaultPalette.length],
			}));

			const jsonColors = colorsToJsonString(newColors);

			const setting: TZoneSetting = {
				zone_setting_enum_id: ZoneSettingEnum.defaultColorPalette,
				value: jsonColors,
				id: colorPaletteSetting?.id,
				description: colorPaletteSetting?.description,
				enabled: colorPaletteSetting.enabled,
				name: colorPaletteSetting.name,
			};

			void ZoneSetting.Update(setting).then(() => {
				addToast('Zone Colors reset to default.', AlertVariant.success);
			});
		} else {
			const jsonColors = colorsToJsonString(colors);

			const setting: TZoneSetting = {
				zone_setting_enum_id: ZoneSettingEnum.defaultColorPalette,
				value: jsonColors,
				id: colorPaletteSetting?.id,
				description: colorPaletteSetting?.description,
				enabled: colorPaletteSetting.enabled,
				name: colorPaletteSetting.name,
			};

			void ZoneSetting.Update(setting).then((response) => {
				addToast('Zone Colors updated successfully.', AlertVariant.success);
			});
		}
	};

	const ColorSwatch = ({ id, color, onClick }: any) => {
		const swatchStyle = {
			width: '36px',
			height: '20px',
			border: '1px solid #999',
			//eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access
			backgroundColor: `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`,
			cursor: 'pointer',
		};

		return (
			<div
				onClick={onClick as React.MouseEventHandler<HTMLDivElement> | undefined}
				style={swatchStyle}
				//eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				title={`Color ${id}`}
			/>
		);
	};

	function hexToRgba(hex: string): { r: number; g: number; b: number; a: number } {
		const bigint = parseInt(hex.slice(1), 16);
		const r = (bigint >> 16) & 255;
		const g = (bigint >> 8) & 255;
		const b = bigint & 255;
		return { r, g, b, a: 1 };
	}

	function rgbToHex(r: number, g: number, b: number): string {
		const toHex = (c: number) => c.toString(16).padStart(2, '0');
		return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
	}

	function colorsToJsonString(
		colors: {
			id: number;
			color: { r: number; g: number; b: number; a: number };
			displayPicker: boolean;
		}[]
	): string {
		const colorMap: Record<string, string> = {};

		colors.forEach((entry, index) => {
			const { r, g, b } = entry.color;
			const hex = rgbToHex(r, g, b);
			colorMap[index.toString()] = hex;
		});

		return JSON.stringify(colorMap, null, 4); // Pretty-print with indentation
	}

	return (
		<Panel style={{ padding: '20px' }}>
			<div style={{ marginBottom: '1rem' }}>
				<Checkbox
					label="Reset Colors to Default on Save"
					id="reset-default-checkbox"
					isChecked={resetOnSave}
					onChange={(checked) => setResetOnSave(checked)}
				/>
			</div>

			<div style={{ display: 'flex', gap: '20rem' }}>
				{/* Left Column */}
				<div>
					{colors.slice(0, 10).map((entry, idx) => (
						<div
							key={entry.id}
							style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
						>
							<span style={{ width: 25 }}>{entry.id}.</span>
							<div style={{ border: 'solid 1px gray', padding: '5px' }}>
								<ColorSwatch
									id={entry.id}
									color={entry.color}
									onClick={() =>
										setColors((prev) =>
											prev.map((c, i) =>
												i === idx
													? { ...c, displayPicker: !c.displayPicker }
													: c
											)
										)
									}
								/>
							</div>
							{entry.displayPicker && (
								<div style={{ position: 'absolute', zIndex: 2 }}>
									<div
										style={{
											position: 'fixed',
											top: 0,
											right: 0,
											bottom: 0,
											left: 0,
										}}
										onClick={() =>
											setColors((prev) =>
												prev.map((c, i) =>
													i === idx ? { ...c, displayPicker: false } : c
												)
											)
										}
									/>
									<div
										style={{
											transform: 'scale(1.25)',
											transformOrigin: 'top left',
										}}
									>
										<ChromePicker
											color={entry.color}
											onChange={(color) =>
												setColors((prev) =>
													prev.map((c, i) =>
														i === idx
															? {
																	...c,
																	color: {
																		r: color.rgb.r,
																		g: color.rgb.g,
																		b: color.rgb.b,
																		a: color.rgb.a ?? 1,
																	},
															  }
															: c
													)
												)
											}
										/>
									</div>
								</div>
							)}
						</div>
					))}
				</div>

				{/* Right Column */}
				<div>
					{colors.slice(10, 20).map((entry, idx) => {
						const realIdx = idx + 10;
						return (
							<div
								key={entry.id}
								style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
							>
								<span style={{ width: 30 }}>{entry.id}.</span>
								<div style={{ border: 'solid 1px gray', padding: '5px' }}>
									<ColorSwatch
										id={entry.id}
										color={entry.color}
										onClick={() =>
											setColors((prev) =>
												prev.map((c, i) =>
													i === realIdx
														? { ...c, displayPicker: !c.displayPicker }
														: c
												)
											)
										}
									/>
								</div>
								{entry.displayPicker && (
									<div style={{ position: 'absolute', zIndex: 2 }}>
										<div
											style={{
												position: 'fixed',
												top: 0,
												right: 0,
												bottom: 0,
												left: 0,
											}}
											onClick={() =>
												setColors((prev) =>
													prev.map((c, i) =>
														i === realIdx
															? { ...c, displayPicker: false }
															: c
													)
												)
											}
										/>
										<div
											style={{
												transform: 'scale(1.25)',
												transformOrigin: 'top left',
											}}
										>
											<ChromePicker
												color={entry.color}
												onChange={(color) =>
													setColors((prev) =>
														prev.map((c, i) =>
															i === realIdx
																? {
																		...c,
																		color: {
																			r: color.rgb.r,
																			g: color.rgb.g,
																			b: color.rgb.b,
																			a: color.rgb.a ?? 1,
																		},
																  }
																: c
														)
													)
												}
											/>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>

			<div style={{ marginTop: '2rem' }}>
				<Button
					style={{ position: 'relative', bottom: '35px' }}
					variant="primary"
					className="pull-right"
					onClick={handleSave}
				>
					Save
				</Button>
			</div>
		</Panel>
	);
}
